/**
 * Frontend helper for the MoreeceSubsXeroStack Lambda
 * (moreece-subs-xero-match). PIN-validated POST that returns suggested
 * Xero matches per active subscription with variance + confidence +
 * conflict flags.
 *
 * Lambda-side cache: 15 min (in-memory in the warm Lambda).
 * Client-side cache: 5 min (this module's _cache) so quickly opening
 * several drawers in a row doesn't re-hit the Lambda.
 *
 * Read-only. Never writes to Xero or Postgres. Approvals/rejections are
 * driven by the caller via authWrite('subscription_events', ...).
 *
 * 2026-05-29.
 */

const URL = import.meta.env.VITE_AWS_SUBS_XERO_MATCH_URL;
const CACHE_MS = 5 * 60 * 1000;

let _cache = null;        // { data, fetchedAt }
let _inFlight = null;     // dedupe concurrent calls — promise

/**
 * Returns the matched-xero payload for ALL active subscriptions in one
 * call. The drawer filters by subscription_id on the result.
 *
 * Throws on auth failure or Lambda error. Caller must handle.
 */
export async function fetchXeroMatches(session) {
  if (!URL) {
    throw new Error('VITE_AWS_SUBS_XERO_MATCH_URL not configured. Check app/.env.local.');
  }
  if (!session?.code) {
    throw new Error('No session PIN — sign in required.');
  }

  // Client-side cache hit
  const now = Date.now();
  if (_cache && (now - _cache.fetchedAt) < CACHE_MS) {
    return _cache.data;
  }

  // Dedupe concurrent in-flight calls
  if (_inFlight) return _inFlight;

  _inFlight = (async () => {
    const res = await fetch(URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin: session.code }),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody?.error || `Xero match failed: HTTP ${res.status}`);
    }
    const json = await res.json();
    _cache = { data: json.data, fetchedAt: now };
    return json.data;
  })();

  try {
    return await _inFlight;
  } finally {
    _inFlight = null;
  }
}

/**
 * Look up the match record for a single subscription by id.
 * Returns null if no entry — UI should render gracefully.
 */
export function findMatchForSub(matchesData, subscriptionId) {
  if (!matchesData?.subscriptions || !subscriptionId) return null;
  return matchesData.subscriptions.find(s => s.subscription_id === subscriptionId) || null;
}

/**
 * Manually invalidate the client-side cache. Call after an Approve/Reject
 * so the next fetch re-runs the algorithm.
 */
export function clearXeroMatchCache() {
  _cache = null;
}

/**
 * Map variance_status → CSS-ready color tokens (background + border + text)
 * for the chip rendered next to the subscription header.
 */
export function varianceChipStyle(status) {
  switch (status) {
    case 'clean':    return { bg: '#dcfce7', border: '#86efac', color: '#166534', label: 'clean' };
    case 'suggest':  return { bg: '#fef3c7', border: '#fde68a', color: '#b45309', label: 'suggest' };
    case 'review':   return { bg: '#fef2f2', border: '#fecaca', color: '#dc2626', label: 'needs review' };
    case 'conflict': return { bg: '#fde2e2', border: '#fca5a5', color: '#991b1b', label: 'needs Hope review' };
    default:         return { bg: '#f4f4f5', border: '#e4e4e7', color: '#52525b', label: status || '—' };
  }
}

/**
 * Has this Xero bank transaction already been approved for this
 * subscription? We check the existing subscription_events.note for the
 * bank_transaction_id substring (the Approve flow writes a note in the
 * form "Xero match: bank_transaction_id=<id> · ...").
 */
export function isAlreadyApproved(drawerEvents, bankTransactionId) {
  if (!bankTransactionId || !Array.isArray(drawerEvents)) return false;
  const needle = `bank_transaction_id=${bankTransactionId}`;
  return drawerEvents.some(ev => typeof ev?.note === 'string' && ev.note.includes(needle));
}
